import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  ArrowLeft, Clock, CreditCard, Landmark, Banknote, CheckCircle,
  Loader2, AlertCircle, QrCode
} from 'lucide-react';

type PaymentMethod = 'card' | 'transfer' | 'cash';

export default function ReservaPago() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const condoId = user?.condoId ?? '';

  // Get reservation data from navigation state (passed by Amenities)
  const state = (location.state as any) || {};
  const amenityName = state.amenityName || 'Amenidad';
  const date = state.date || '';
  const startTime = state.startTime || '';
  const endTime = state.endTime || '';
  const cost = state.cost || 0;
  const bookingId = state.bookingId;

  const [method, setMethod] = useState<PaymentMethod>('card');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any>(null);

  const formatCurrency = (amount: number) => {
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
    } catch {
      return `$${amount.toFixed(2)} MXN`;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo no puede exceder 5MB');
        return;
      }
      setProofFile(file);
    }
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (method !== 'card' && !proofFile && method === 'transfer') {
      setError('El comprobante es obligatorio para transferencia');
      return;
    }

    setSubmitting(true);
    try {
      if (method === 'card') {
        // Route to Openpay checkout with reservation context
        const params = new URLSearchParams({
          plan: 'reservation',
          bookingId: bookingId || '',
          amount: String(cost),
          description: `Reserva: ${amenityName} ${formatDate(date)}`,
          condoId,
        });
        window.location.href = `/billing/checkout?${params.toString()}`;
        return;
      }

      // Transfer / cash — record payment with proof
      const formData = new FormData();
      formData.append('charge_id', bookingId || '');
      formData.append('amount', String(cost));
      formData.append('currency', 'MXN');
      formData.append('payment_method', method);
      formData.append('reference_number', referenceNumber);
      if (proofFile) formData.append('proof_image', proofFile);
      formData.append('notes', `Pago de reserva: ${amenityName} - ${date} ${startTime}-${endTime}`);

      const res = await api.post('/financial/payments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess(res.data?.data ?? res.data);
    } catch (err: any) {
      console.error('Payment error:', err);
      const msg = err?.response?.data?.error || 'Error al procesar el pago';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center p-4">
        <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)] mb-2">¡Pago enviado!</h2>
          <p className="text-[var(--v-text-secondary)] mb-6">
            {success.status === 'paid'
              ? 'Tu reserva ha sido confirmada.'
              : 'Tu pago está pendiente de verificación.'}
          </p>
          {success.receipt_number && (
            <div className="bg-[var(--v-base-bg)] p-4 rounded-xl mb-6">
              <p className="text-sm text-[var(--v-text-secondary)]">Recibo</p>
              <p className="text-xl font-bold font-mono text-[var(--v-text-primary)]">#{success.receipt_number}</p>
            </div>
          )}
          {/* QR */}
          <div className="bg-white p-4 rounded-xl mb-6 inline-block">
            <div className="w-36 h-36 bg-slate-100 rounded-lg flex items-center justify-center">
              <QrCode className="w-28 h-28 text-slate-800" />
            </div>
            <p className="text-xs text-[var(--v-text-secondary)] mt-2">Muestra este código en la caseta</p>
          </div>
          <button
            onClick={() => navigate('/building/amenities')}
            className="w-full px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Volver a Amenidades
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] text-[var(--v-text-primary)]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate('/building/amenities')}
          className="flex items-center gap-2 text-sm text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a Amenidades
        </button>

        <h1 className="text-2xl font-bold mb-1">Pagar Reserva</h1>
        <p className="text-[var(--v-text-secondary)] mb-6">{amenityName}</p>

        {/* Reservation Summary */}
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-6 mb-6">
          <h3 className="font-bold mb-4">Resumen de la reserva</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--v-text-secondary)]">Amenidad</span>
              <span className="font-medium">{amenityName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--v-text-secondary)]">Fecha</span>
              <span className="font-medium">{formatDate(date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--v-text-secondary)]">Horario</span>
              <span className="font-medium">{startTime} - {endTime}</span>
            </div>
            <div className="border-t border-[var(--v-border-light)] pt-2 mt-2 flex justify-between">
              <span className="font-bold">Total</span>
              <span className="font-bold text-[var(--v-info)]">{formatCurrency(cost)}</span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <form onSubmit={handlePay} className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-6 space-y-4">
          <h3 className="font-bold">Método de pago</h3>
          <div className="space-y-2">
            <MethodOption icon={<CreditCard className="w-5 h-5" />} label="Tarjeta" description="Visa, Mastercard, Amex (Openpay)" selected={method === 'card'} onClick={() => setMethod('card')} />
            <MethodOption icon={<Landmark className="w-5 h-5" />} label="Transferencia" description="SPEI bancario" selected={method === 'transfer'} onClick={() => setMethod('transfer')} />
            <MethodOption icon={<Banknote className="w-5 h-5" />} label="Efectivo" description="OXXO o administración" selected={method === 'cash'} onClick={() => setMethod('cash')} />
          </div>

          {method !== 'card' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Referencia {method === 'transfer' && '(obligatorio)'}
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg bg-[var(--v-surface-1)] text-[var(--v-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Folio de transferencia/pago"
                  required={method === 'transfer'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Comprobante {method === 'transfer' && '(obligatorio)'}
                </label>
                <div className="border-2 border-dashed border-[var(--v-border-strong)] rounded-lg p-6 text-center">
                  {proofFile ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm truncate">{proofFile.name}</span>
                      <button type="button" onClick={() => setProofFile(null)} className="text-red-500">
                        <ArrowLeft className="w-4 h-4 rotate-45" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input type="file" accept="image/*,.pdf" onChange={handleFileChange} className="hidden" id="proof-upload" required={method === 'transfer'} />
                      <label htmlFor="proof-upload" className="cursor-pointer text-sm text-[var(--v-info)] hover:underline">
                        Subir comprobante (imagen o PDF, máx 5MB)
                      </label>
                    </>
                  )}
                </div>
                <p className="text-xs text-[var(--v-text-tertiary)] mt-1">Tu pago quedará pendiente de verificación.</p>
              </div>
            </>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-3 bg-[var(--v-info)] hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium flex items-center justify-center gap-2 min-h-11"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Procesando...</>
            ) : method === 'card' ? (
              <>Pagar {formatCurrency(cost)}</>
            ) : (
              'Enviar comprobante'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function MethodOption({ icon, label, description, selected, onClick }: {
  icon: React.ReactNode; label: string; description: string; selected: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
        selected ? 'border-[var(--v-info)] bg-blue-50' : 'border-[var(--v-border-light)] hover:border-[var(--v-border-strong)]'
      }`}
    >
      <div className={`flex-shrink-0 ${selected ? 'text-[var(--v-info)]' : 'text-[var(--v-text-secondary)]'}`}>{icon}</div>
      <div className="flex-1">
        <div className="font-medium text-[var(--v-text-primary)]">{label}</div>
        <div className="text-sm text-[var(--v-text-secondary)]">{description}</div>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${selected ? 'border-[var(--v-info)] bg-[var(--v-info)]' : 'border-[var(--v-border-strong)]'}`}>
        {selected && <CheckCircle className="w-full h-full text-white p-0.5" />}
      </div>
    </button>
  );
}
